/** Buscador de clientes por razon social o RUC (usa la lista del tenant activo). */
import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { MobileIcon } from './MobileIcon';
import { useMobileClientsQuery } from '@/hooks/useMobileClients';

export type ClientAutocompleteProps = {
  value: string | null;
  onChange: (clientId: string | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  isRequired?: boolean;
};

const MAX_RESULTS = 20;

export function ClientAutocomplete({
  value,
  onChange,
  label = 'Cliente',
  placeholder = 'Buscar por razón social o RUC...',
  error,
  disabled = false,
  isRequired = false,
}: ClientAutocompleteProps) {
  const [search, setSearch] = useState('');
  const clientsQuery = useMobileClientsQuery();
  const items = useMemo(() => clientsQuery.data?.items ?? [], [clientsQuery.data]);

  const selected = useMemo(
    () => items.find((client) => client.id === value) ?? null,
    [items, value],
  );

  const results = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') return items.slice(0, MAX_RESULTS);
    return items
      .filter(
        (client) =>
          client.legal_name.toLowerCase().includes(query) ||
          (client.tax_id ?? '').toLowerCase().includes(query),
      )
      .slice(0, MAX_RESULTS);
  }, [items, search]);

  return (
    <div className="w-full">
      <label
        htmlFor="client-autocomplete-search"
        className={clsx(
          'mb-1.5 block truncate text-sm font-medium',
          error ? 'text-error' : 'text-text-secondary',
        )}
      >
        {label}
        {isRequired && (
          <span aria-hidden="true" className="text-error">
            {' '}
            *
          </span>
        )}
      </label>

      {selected && (
        <div className="mb-2 flex min-h-[52px] items-center gap-2 rounded-xl border-2 border-primary bg-primary-light px-3 py-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
            <MobileIcon name="business" className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-text-primary">
              {selected.legal_name}
            </span>
            {selected.tax_id && (
              <span className="block truncate text-xs text-text-secondary">
                RUC {selected.tax_id}
              </span>
            )}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setSearch('');
              onChange(null);
            }}
            aria-label={`Quitar ${selected.legal_name}`}
            className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-primary hover:bg-primary-light disabled:opacity-40"
          >
            Cambiar
          </button>
        </div>
      )}

      <div
        className={clsx(
          'flex min-h-[52px] items-center gap-2 rounded-xl border-2 px-3 transition-all',
          error
            ? 'border-error bg-error-light'
            : 'border-border bg-white hover:border-border-dark focus-within:border-primary focus-within:shadow-sm',
        )}
      >
        <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center text-text-tertiary">
          <MobileIcon name="search" className="h-5 w-5" />
        </span>
        <input
          id="client-autocomplete-search"
          type="search"
          value={search}
          disabled={disabled || clientsQuery.isPending}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={clientsQuery.isPending ? 'Cargando clientes...' : placeholder}
          aria-invalid={error ? true : undefined}
          className="h-12 min-h-[44px] w-full min-w-0 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
        {search !== '' && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Limpiar búsqueda de cliente"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-tertiary"
          >
            <MobileIcon name="close" className="h-5 w-5" />
          </button>
        )}
      </div>

      {clientsQuery.isError && (
        <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
          <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
          <p className="min-w-0 flex-1 break-words text-sm text-error">
            No se pudo cargar la lista de clientes.
          </p>
          <button
            type="button"
            onClick={() => void clientsQuery.refetch()}
            className="flex min-h-[44px] shrink-0 items-center rounded-lg px-3 text-sm font-semibold text-error"
          >
            Reintentar
          </button>
        </div>
      )}

      {!clientsQuery.isPending && !clientsQuery.isError && (
        <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto" aria-label="Clientes disponibles">
          {results.map((client) => {
            const isSelected = client.id === value;
            return (
              <li key={client.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(isSelected ? null : client.id)}
                  aria-pressed={isSelected}
                  className={clsx(
                    'flex min-h-[52px] w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors disabled:opacity-40',
                    isSelected
                      ? 'border-primary bg-primary-light'
                      : 'border-border-light bg-white hover:border-border-dark hover:bg-bg-tertiary',
                  )}
                >
                  <span
                    className={clsx(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                      isSelected ? 'bg-primary text-white' : 'bg-bg-tertiary text-text-secondary',
                    )}
                  >
                    <MobileIcon name="business" className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-text-primary">
                      {client.legal_name}
                    </span>
                    {client.tax_id && (
                      <span className="block truncate text-xs text-text-secondary">
                        RUC {client.tax_id}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <MobileIcon name="check_circle" className="h-5 w-5 shrink-0 text-primary" />
                  )}
                </button>
              </li>
            );
          })}
          {results.length === 0 && (
            <li className="flex flex-col items-center gap-1 rounded-card bg-white px-4 py-6 text-center shadow-card">
              <MobileIcon name="search_off" className="h-8 w-8 text-text-tertiary" />
              <p className="truncate text-sm font-semibold text-text-primary">Sin clientes</p>
              <p className="text-sm text-text-secondary">
                {search.trim() === ''
                  ? 'Aún no hay clientes registrados.'
                  : 'Ninguno coincide con tu búsqueda.'}
              </p>
            </li>
          )}
        </ul>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-error">
          <MobileIcon name="error" className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{error}</span>
        </p>
      )}
    </div>
  );
}
